import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonComponent } from '@libs/common/components';
import { ItemBaseComponent } from '@libs/form/components';
import { IconComponent } from '@libs/components/icons';
import type { SrmFileModel, SrmTicketSuggestionItemModel, SrmTicketSuggestionModel, SrmTicketSuggestionResourceModel } from '@libs/services/models';
import { ViewportService } from '@libs/shared/services';
import { DownloadTrackingService } from '@libs/shared/tracking';
import { firstValueFrom } from 'rxjs';
import { SrmHelperService } from '../srm-helper.service';
import { SrmNavigationService } from '../srm-navigation.service';
import { SrmService } from '../services';
import { SrmCreateFormService } from '../srm-create-form/srm-create-form.service';

const SRM_TICKET_SUGGESTION_TEXTS: Record<string, string> = {
  found: 'Passende Dokumente mit KI gefunden',
  notFound: 'Leider konnten keine passenden Dokumente mit KI gefunden werden.',
  searching: 'Suche mit KI nach passenden Dokumenten...',
  solvedButton: 'Problem gelöst',
};

@Component({
  selector: 'ui-srm-ticket-suggestion',
  templateUrl: './srm-ticket-suggestion.component.html',
  styleUrls: ['./srm-ticket-suggestion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    ButtonComponent,
  ],
})
export class SrmTicketSuggestionComponent extends ItemBaseComponent<SrmTicketSuggestionItemModel> implements OnInit {
  @Input() public data!: SrmTicketSuggestionItemModel;

  protected readonly isMobile: Signal<boolean> = toSignal(inject(ViewportService).isMobile(), { initialValue: false });

  protected readonly isLoading: WritableSignal<boolean> = signal(true);
  protected readonly suggestion: WritableSignal<SrmTicketSuggestionModel | undefined> = signal(undefined);

  protected readonly selectedResource: WritableSignal<SrmTicketSuggestionResourceModel | undefined> = signal(undefined);
  protected readonly isLoadingFile: WritableSignal<boolean> = signal(false);
  protected readonly isSolvedEnabled: Signal<boolean> = computed(() => Boolean(this.selectedResource()));

  protected readonly solvedButtonText: Signal<string> = signal(SRM_TICKET_SUGGESTION_TEXTS['solvedButton']);
  protected readonly searchForSuggestionText: Signal<string> = signal(SRM_TICKET_SUGGESTION_TEXTS['searching']);
  protected readonly titleText: Signal<string> = computed(() => {
    if (this.isLoading()) {
      return SRM_TICKET_SUGGESTION_TEXTS['searching'];
    }

    return this.suggestion()?.available
      ? SRM_TICKET_SUGGESTION_TEXTS['found']
      : SRM_TICKET_SUGGESTION_TEXTS['notFound'];
  });

  private readonly srmService: SrmService = inject(SrmService);
  private readonly srmHelperService: SrmHelperService = inject(SrmHelperService);
  private readonly downloadTracking: DownloadTrackingService = inject(DownloadTrackingService);
  private readonly srmFormService: SrmCreateFormService = inject(SrmCreateFormService);
  private readonly router: Router | null = inject(Router);
  private readonly navigation: SrmNavigationService = inject(SrmNavigationService);

  public ngOnInit(): void {
    void this.loadSuggestion();
  }

  protected async onResourceClick(resource: SrmTicketSuggestionResourceModel): Promise<void> {
    this.selectedResource.set(resource);

    if (resource.type === 'link') {
      window.open(resource.reference, '_blank', 'noopener');

      return;
    }

    this.isLoadingFile.set(true);

    try {
      const file: SrmFileModel = await firstValueFrom(this.srmService.getTicketSuggestionFile(resource.reference));
      const fileName: string = file.fileName ?? resource.name;

      this.downloadTracking.trackDownload(fileName);

      await this.srmHelperService.openBase64File(file.base64, fileName);
    } finally {
      this.isLoadingFile.set(false);
    }
  }

  protected onSolvedClick(): void {
    void this.router?.navigate([this.navigation.overviewPath()]);
  }

  private async loadSuggestion(): Promise<void> {
    this.isLoading.set(true);

    try {
      const suggestion: SrmTicketSuggestionModel = await this.srmFormService.requestTicketSuggestion();

      this.suggestion.set(suggestion.available ? suggestion : undefined);
    } catch {
      this.suggestion.set(undefined);
    } finally {
      this.isLoading.set(false);
    }
  }
}
